/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useCallback, useMemo, useRef } from 'react';

import { Separator, Spinner } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { useI18n, withI18nPlaceholders } from '@change-corgi/core/react/i18n';

import I18N_PLACEHOLDERS from 'config/i18n/placeholders/petitionDetails/petition-details.yml';

import { usePetitionDetailsPageData } from 'src/app/pages/petitionDetails/pageContext';
import { focus } from 'src/app/shared/utils/dom';

import { usePetitionShareCounts } from '../../../../hooks/shareCounts';
import { SECTION_ID_MAPPING, useScrollToSection } from '../../../../hooks/useScrollToSection';
import { useSpotlightCardContent } from '../../hooks/useSpotlightCardContent';
import { useMomentumBarTracking } from '../../hooks/useTracking';
import { filterVideoComments, getFirstVideoThumbnail, hasVideoComments } from '../../utils';
import { CounterUpdate } from '../CounterUpdate';
import { MediaOutletLogos } from '../MediaOutletLogos';
import { SignersCarousel } from '../SignersCarousel';
import { SpotlightCard } from '../SpotlightCard';
import { VideoPreviewButton } from '../VideoPreviewButton';

const MomentumBarComponent: React.FC = () => {
	const {
		data: {
			petition: { id: petitionId, commentsConnection, recentPublicSignersConnectionV2 },
			supporterVoicesVideos,
			mediaSourcesPage,
		},
	} = usePetitionDetailsPageData();
	const { translate } = useI18n();
	const tracking = useMomentumBarTracking();
	const spotlightItems = useSpotlightCardContent();
	const { totalShareCount, isLoading: isShareCountsLoading } = usePetitionShareCounts();
	const hasTrackedPreviewView = useRef(false);

	const handleVideoPreviewAccess = useCallback(() => {
		if (hasTrackedPreviewView.current) return;
		hasTrackedPreviewView.current = true;
		tracking.trackVideoPreviewView(petitionId);
	}, [petitionId, tracking]);

	const handleVideoPreviewBlur = useCallback(() => {
		hasTrackedPreviewView.current = false;
	}, []);

	const {
		actions: { scrollToSection },
	} = useScrollToSection();
	const handleVideoPreviewClick = useCallback(() => {
		scrollToSection(SECTION_ID_MAPPING.SUPPORTER_VOICES);
		focus(document.querySelector<HTMLElement>(`#${SECTION_ID_MAPPING.SUPPORTER_VOICES}`), {
			makeFocusable: true,
			preventScroll: true,
		});
	}, [scrollToSection]);

	const handleMediaLogosClick = useCallback(() => {
		tracking.trackMediaClick(petitionId);
		scrollToSection(SECTION_ID_MAPPING.MEDIA_MENTIONS);
		focus(document.querySelector<HTMLElement>(`#${SECTION_ID_MAPPING.MEDIA_MENTIONS}`), {
			makeFocusable: true,
			preventScroll: true,
		});
	}, [petitionId, scrollToSection, tracking]);

	const commentsCount = commentsConnection.totalCount || 0;
	const sharesCount = totalShareCount ?? 0;
	const recentSigners = recentPublicSignersConnectionV2.edges;

	const { mediaSources, numberOfMediaSources } = mediaSourcesPage;
	const mediaMentionsCount = numberOfMediaSources;
	const hasMediaMentions = mediaMentionsCount > 0;

	const { videos } = supporterVoicesVideos;
	const hasVideos = hasVideoComments(videos);
	const firstVideoThumbnail = useMemo(() => getFirstVideoThumbnail(videos, { width: 280, height: 420 }), [videos]);
	const firstVideoThumbnailAlt = useMemo(() => {
		const firstVideo = filterVideoComments(videos)[0];
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		const name = firstVideo?.user?.shortDisplayName;
		return name ? `Video thumbnail from supporter ${name}` : 'Supporter video thumbnail';
	}, [videos]);

	// Show a loader without the bordered container until share counts have loaded.
	if (isShareCountsLoading) {
		return (
			<div className="mb-8 flex min-h-[120px] w-full items-center justify-center" data-qa="momentum-bar-loading">
				<Spinner className="size-6" />
			</div>
		);
	}

	// Require at least comments, plus either shares or media mentions. Hide bar if no shares and no media mentions.
	if (commentsCount < 1 || (sharesCount === 0 && !hasMediaMentions)) {
		return null;
	}

	return (
		<div
			className="mb-8 flex flex-col items-start self-stretch rounded-lg border border-[#E1DDE9] p-3 md:p-0 md:pt-4 md:pr-3 md:pb-0 md:pl-4"
			data-qa="momentum-bar-container"
		>
			<div className="flex w-full flex-col items-start gap-0.5 p-0 lg:flex-row">
				<SpotlightCard
					items={spotlightItems}
					animatedCardsCarousel
					testId="momentum-bar-spotlight"
					qaId="momentum-bar-spotlight"
				/>
				<div
					className={cn(
						'flex w-full items-start gap-[10px] lg:w-[40%] lg:gap-0',
						hasMediaMentions ? 'flex-row' : 'flex-col lg:flex-row',
					)}
					data-testid="momentum-bar-submodules"
					data-qa="momentum-bar-submodules"
				>
					{!hasMediaMentions && sharesCount > 0 && (
						<CounterUpdate
							label={translate('corgi.petition_gamma.momentum_bar.shares.label')}
							count={sharesCount}
							testId="momentum-bar-shares"
							qaId="momentum-bar-shares"
						/>
					)}
					{hasMediaMentions && (
						<div className="min-w-0 flex-1">
							<CounterUpdate
								label={translate('corgi.petition_gamma.momentum_bar.media_mentions.label')}
								count={mediaMentionsCount}
								testId="momentum-bar-media-mentions"
								qaId="momentum-bar-media-mentions"
								sectionId={SECTION_ID_MAPPING.MEDIA_MENTIONS}
								onTrackClick={() => tracking.trackMediaClick(petitionId)}
								stackLabelOnMobile
							>
								<MediaOutletLogos
									mediaSources={mediaSources}
									testIdPrefix="momentum-bar"
									onLogoClick={handleMediaLogosClick}
								/>
							</CounterUpdate>
						</div>
					)}
					{(() => {
						const commentsCounter = (
							<CounterUpdate
								label={translate('corgi.petition_gamma.momentum_bar.comments.label')}
								count={commentsCount}
								testId="momentum-bar-comments"
								qaId="momentum-bar-comments"
								sectionId={SECTION_ID_MAPPING.SUPPORTER_VOICES}
								onTrackClick={() => tracking.trackCommentsClick(petitionId)}
								stackLabelOnMobile={hasMediaMentions}
							>
								{hasVideos && firstVideoThumbnail && (
									<VideoPreviewButton
										thumbnailAlt={firstVideoThumbnailAlt}
										thumbnailUrl={firstVideoThumbnail}
										ariaLabel={translate('corgi.petition_details.momentum_bar.video_preview_aria_label')}
										onAccess={handleVideoPreviewAccess}
										onBlur={handleVideoPreviewBlur}
										onClick={handleVideoPreviewClick}
										testIdPrefix="momentum-bar"
									/>
								)}
							</CounterUpdate>
						);
						return hasMediaMentions ? <div className="min-w-0 flex-1">{commentsCounter}</div> : commentsCounter;
					})()}
				</div>
			</div>
			<Separator className="mt-[10px] lg:hidden" />
			<div className="w-full max-w-full min-w-0 overflow-hidden motion-reduce:hidden">
				<SignersCarousel signers={recentSigners} testId="momentum-bar-signers-row" qaId="momentum-bar-signers-row" />
			</div>
		</div>
	);
};

export const MomentumBar = withI18nPlaceholders(I18N_PLACEHOLDERS, MomentumBarComponent);
